/** The systems the contest is opened on: in the applications, and in a browser. */
export type Platform = 'ios' | 'android' | 'macos' | 'windows';

const PLATFORM_NAMES: Record<Platform, string> = {
    ios: 'iOS',
    android: 'Android',
    macos: 'macOS',
    windows: 'Windows',
};

/**
 * A font a contest can show without an upload: one a device ships with, or
 * one the web application bundles. Where it is missing, the device shows the
 * first family of `fallback` it has.
 */
export interface SystemFont {
    family: string;
    fallback: string;
    platforms: readonly Platform[];
}

const APPLE_AND_WINDOWS: readonly Platform[] = ['ios', 'macos', 'windows'];
const APPLE: readonly Platform[] = ['ios', 'macos'];

const EVERYWHERE: readonly Platform[] = ['ios', 'android', 'macos', 'windows'];

/**
 * The fonts a contest can pick from a list. ACT Easy, the brand font, comes
 * first: the web application bundles it, so every system shows it, and a
 * contest without a theme shows it. The others are installed with the
 * systems a contest is opened on. None of those is on all four: Android ships
 * Roboto and Noto, and little else, so each falls back to the closest font
 * Android has.
 */
export const SYSTEM_FONTS: readonly SystemFont[] = [
    {
        family: 'ACT Easy',
        fallback: '"Geist Variable", system-ui, sans-serif',
        platforms: EVERYWHERE,
    },
    {
        family: 'Helvetica Neue',
        fallback: 'Arial, Roboto, sans-serif',
        platforms: APPLE,
    },
    {
        family: 'Arial',
        fallback: 'Helvetica, Roboto, sans-serif',
        platforms: APPLE_AND_WINDOWS,
    },
    {
        family: 'Verdana',
        fallback: 'Geneva, Roboto, sans-serif',
        platforms: APPLE_AND_WINDOWS,
    },
    {
        family: 'Trebuchet MS',
        fallback: 'Verdana, Roboto, sans-serif',
        platforms: APPLE_AND_WINDOWS,
    },
    {
        family: 'Avenir Next',
        fallback: 'Avenir, "Segoe UI", Roboto, sans-serif',
        platforms: APPLE,
    },
    {
        family: 'Futura',
        fallback: '"Century Gothic", Roboto, sans-serif',
        platforms: APPLE,
    },
    {
        family: 'Gill Sans',
        fallback: '"Gill Sans MT", Calibri, Roboto, sans-serif',
        platforms: APPLE,
    },
    {
        family: 'Roboto',
        fallback: '"Helvetica Neue", Arial, sans-serif',
        platforms: ['android'],
    },
    {
        family: 'Segoe UI',
        fallback: '"Helvetica Neue", Roboto, sans-serif',
        platforms: ['windows'],
    },
    {
        family: 'Tahoma',
        fallback: 'Verdana, Roboto, sans-serif',
        platforms: ['macos', 'windows'],
    },
    {
        family: 'Impact',
        fallback: '"Arial Black", Roboto, sans-serif',
        platforms: ['macos', 'windows'],
    },
    {
        family: 'Georgia',
        fallback: '"Times New Roman", "Noto Serif", serif',
        platforms: APPLE_AND_WINDOWS,
    },
    {
        family: 'Times New Roman',
        fallback: 'Times, "Noto Serif", serif',
        platforms: APPLE_AND_WINDOWS,
    },
    {
        family: 'Palatino',
        fallback: '"Palatino Linotype", Georgia, "Noto Serif", serif',
        platforms: APPLE,
    },
    {
        family: 'Noto Serif',
        fallback: 'Georgia, "Times New Roman", serif',
        platforms: ['android'],
    },
    {
        family: 'Courier New',
        fallback: 'Courier, "Droid Sans Mono", monospace',
        platforms: APPLE_AND_WINDOWS,
    },
];

export function systemFontOf(family: string): SystemFont | undefined {
    return SYSTEM_FONTS.find((font) => font.family === family);
}

/** A font missing from both phone systems is safe only in a desktop browser. */
export function isDesktopOnly(font: SystemFont): boolean {
    return (
        !font.platforms.includes('ios') && !font.platforms.includes('android')
    );
}

/** Where the font is missing, as the picker says it. Empty when it is nowhere missing. */
export function missingOn(font: SystemFont): string {
    if (isDesktopOnly(font)) {
        return 'Desktop only';
    }
    const missing = (Object.keys(PLATFORM_NAMES) as Platform[]).filter(
        (platform) => !font.platforms.includes(platform)
    );
    return missing.length > 0
        ? `Not on ${missing
              .map((platform) => PLATFORM_NAMES[platform])
              .join(', ')}`
        : '';
}

/** The stack that shows a font, then its fallback. */
export function fontStack(font: { family: string; fallback: string }): string {
    return `"${font.family}", ${font.fallback}`;
}
